<template>
  <div id="settings" class="rbac-page wrrapper mb-1">
    <page-heading title="Role-Based Access Control" border>
      <template #content>
        <div class="rbac-actions">
          <Button
            v-if="activeTab === 'roles'"
            :onClick="showPanelModal"
            className="button--primary rbac-action"
            title="Add Role"
          ></Button>
          <Button
            v-else-if="editButtonText !== 'Save Permissions'"
            :onClick="editBusinessPermissions"
            className="button--primary rbac-action"
            title="Edit Permissions"
          ></Button>
          <Button
            v-else
            :onClick="savePermissions"
            className="button--primary rbac-action"
            title="Save Changes"
          ></Button>
        </div>
      </template>
    </page-heading>

    <div class="rbac-layout">
      <aside class="rbac-nav">
        <button
          type="button"
          class="rbac-tab"
          :class="{ active: activeTab === 'roles' }"
          @click="handleRoleClick"
        >
          <span class="mdi mdi-account-group-outline"></span>
          <span>Roles</span>
        </button>
        <button
          type="button"
          class="rbac-tab"
          :class="{ active: activeTab === 'permissions' }"
          @click="handlePermissionClick('permissions')"
        >
          <span class="mdi mdi-routes"></span>
          <span>Route Permissions</span>
        </button>
        <button
          v-if="userInfo && userInfo.full_name === 'ady_Uc'"
          type="button"
          class="rbac-tab"
          :class="{ active: activeTab === 'route_permissions' }"
          @click="routePermissionsClick('route_permissions')"
        >
          <span class="mdi mdi-shield-key-outline"></span>
          <span>Module Permissions</span>
        </button>
      </aside>

      <main class="rbac-content">
        <section class="rbac-header">
          <div>
            <p class="rbac-kicker">{{ sectionMeta.kicker }}</p>
            <h2>{{ sectionMeta.title }}</h2>
          </div>
          <span v-if="activeTab !== 'roles'" class="rbac-mode">
            {{ disablePermissions ? "Read-only" : "Editing" }}
          </span>
        </section>

        <div class="rbac-metrics">
          <div class="rbac-metric">
            <span>Roles</span>
            <strong>{{ roleCount }}</strong>
          </div>
          <div class="rbac-metric">
            <span>Modules</span>
            <strong>{{ moduleCount }}</strong>
          </div>
          <div class="rbac-metric">
            <span>Routes</span>
            <strong>{{ routeCount }}</strong>
          </div>
        </div>

        <modal
          title="New Role"
          name="addnewrole"
          class="custom-modal"
          height="auto"
          width="600"
          :adaptive="true"
        >
          <template v-slot:content>
            <div class="rbac-modal-body">
              <form @submit.prevent class="create-service mb-base">
                <Input
                  class="mb-base"
                  label="Name"
                  v-model="role.name"
                  type="text"
                />
                <TextArea
                  styles="mb-base"
                  label="Description"
                  rows="3"
                  name="narration"
                  v-model="role.description"
                />
                <Select
                  styles="invoice-select"
                  label="Parent"
                  class="mb-2"
                  :options="parentRoles"
                  :input="val => (role.parentId = val.key)"
                  :clearable="false"
                  placeholder="Select"
                />
                <div class="rbac-modal-actions">
                  <Button
                    className="button--transparent custom-button"
                    title="Cancel"
                    :onClick="handleCancel"
                  ></Button>
                  <Button
                    className="button--primary custom-button"
                    title="Create Role"
                    :loader="loading"
                    :onClick="handleCreate"
                  ></Button>
                </div>
              </form>
            </div>
          </template>
        </modal>

        <div v-if="activeTab === 'roles'" class="rbac-section">
          <data-table
            :cols="columns"
            :rows="Roles"
            height="calc(100vh - 22rem)"
            :loading="loading"
          >
          </data-table>
        </div>

        <div v-if="activeTab === 'route_permissions'" class="rbac-section">
          <div v-if="loading" class="rbac-loading">
            <Loader :loading="loading" />
          </div>
          <div v-else class="permission-card">
            <div class="permission-table-wrap">
              <table class="permissions-table">
                <thead>
                  <tr>
                    <th class="permission-name">Module</th>
                    <th
                      v-for="role in listDoctors"
                      :key="role.id"
                      class="permission-role"
                    >
                      {{ role.name }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(permission, index) in modulePermissions"
                    :key="index"
                  >
                    <td class="permission-name">
                      <span>{{ permission.moduleName }}</span>
                    </td>
                    <td
                      class="permission-check"
                      v-for="role in listDoctors"
                      :key="role.id"
                    >
                      <input
                        class="permission-checkbox"
                        :disabled="disablePermissions"
                        @change="
                          handlePermissionChange(role, permission, $event)
                        "
                        :checked="moduleCheck(role.name, permission)"
                        type="checkbox"
                      />
                    </td>
                  </tr>
                  <tr v-if="!modulePermissions.length">
                    <td :colspan="roleCount + 1" class="permission-empty">
                      No module permissions found.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'permissions'" class="rbac-section">
          <div v-if="loading" class="rbac-loading">
            <Loader :loading="loading" />
          </div>
          <div v-else class="permissions-layout">
            <aside class="module-list">
              <div class="module-list-title">Modules</div>
              <button
                v-for="mode in moduleTabs"
                :key="mode"
                type="button"
                class="module-pill"
                :class="{ active: mode === selectedModule }"
                @click="() => handleSubTabClick(mode)"
              >
                {{ mode }}
              </button>
            </aside>

            <div class="permission-card">
              <div class="permission-card-header">
                <span>{{ selectedModule }}</span>
                <strong>{{ routeCount }} routes</strong>
              </div>
              <div class="permission-table-wrap">
                <table class="permissions-table">
                  <thead>
                    <tr>
                      <th class="permission-name">Route</th>
                      <th
                        v-for="role in listDoctors"
                        :key="role.id"
                        class="permission-role"
                      >
                        {{ role.name }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(route, index) in routesList" :key="index">
                      <td class="permission-name">
                        <span>{{ route.routeName }}</span>
                      </td>
                      <td
                        class="permission-check"
                        v-for="role in listDoctors"
                        :key="role.id"
                      >
                        <input
                          class="permission-checkbox"
                          :disabled="disablePermissions"
                          @change="handleRoleChange(role, route, $event)"
                          :checked="checkItem(role.name, route.permissions)"
                          type="checkbox"
                        />
                      </td>
                    </tr>
                    <tr v-if="!routesList.length">
                      <td :colspan="roleCount + 1" class="permission-empty">
                        No routes found.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>
<script>
import Button from "@/components/Button";
import Loader from "@/components/Loader";
import DataTable from "@/components/DataTable";
import Modal from "@/components/Modal";
import Select from "@/components/Select.vue";
import Input from "@/components/Input";
import TextArea from "@/components/TextArea.vue";

import { mapActions, mapGetters } from "vuex";
import { formatedDate } from "../../../utils/helpers";

export default {
  components: {
    Button,
    DataTable,
    Modal,
    Loader,
    Select,
    TextArea,
    Input
  },
  data() {
    return {
      activeTab: "roles",
      showSubTabs: false,
      selectedModule: "",
      disablePermissions: true,
      editButtonText: "Edit Permissions",
      routes: [],
      role: {},
      modules: [],
      permissions: {},
      userModules: {},
      modulePermissions: [],
      mp: [
        {
          moduleName: "Patient",
          visibility: true,
          permissions: {
            doctor: true,
            finance: true,
            technician: true,
            receptionist: true
          }
        },
        {
          moduleName: "Registrations",
          visibility: true,
          permissions: {
            doctor: true,
            finance: true,
            technician: true,
            receptionist: true
          }
        },
        {
          moduleName: "Charges",
          visibility: true,
          permissions: {
            doctor: true,
            finance: true,
            technician: true,
            receptionist: true
          }
        },
        {
          moduleName: "Expenses",
          visibility: true,
          permissions: {
            doctor: true,
            finance: true,
            technician: true,
            receptionist: true
          }
        },
        {
          moduleName: "Lookups",
          visibility: true,
          permissions: {
            doctor: true,
            finance: true,
            technician: true,
            receptionist: true
          }
        },
        {
          moduleName: "Services",
          visibility: true,
          permissions: {
            doctor: true,
            finance: true,
            technician: true,
            receptionist: true
          }
        },
        {
          moduleName: "Settings",
          visibility: true,
          permissions: {
            doctor: true,
            finance: true,
            technician: true,
            receptionist: true
          }
        },
        {
          moduleName: "Template",
          visibility: true,
          permissions: {
            doctor: true,
            finance: true,
            technician: true,
            receptionist: true
          }
        },
        {
          moduleName: "User",
          visibility: true,
          permissions: {
            doctor: true,
            finance: true,
            technician: true,
            receptionist: true
          }
        },
        {
          moduleName: "Report",
          visibility: true,
          permissions: {
            doctor: true,
            finance: true,
            technician: true,
            receptionist: true
          }
        },
        {
          moduleName: "Patient Report",
          visibility: true,
          permissions: {
            doctor: true,
            finance: true,
            technician: true,
            receptionist: true
          }
        }
      ],
      columns: [
        { column: "serial", label: "#", width: "60" },
        {
          column: "name",
          label: "Name",
          sortable: true
          // link: true,
          // routerName: "BranchSetting",
        },

        {
          column: "description",
          label: "Description"
          //   sortable: true,
          //   sort: true,
        },
        {
          column: "created_on",
          label: "Created On"
          //   sortable: true,
          //   sort: true,
        },
        {
          column: "parent",
          label: "Parent"
          //   sortable: true,
          //   sort: true,
        }
      ]
    };
  },
  computed: {
    ...mapGetters({
      loading: "loading",
      lookupEndPoints: "bussiness/lookupEndPoints",
      rbacPermissions: "bussiness/rbacPermissions",
      listDoctors: "doctors/listDoctors",
      userInfo: "auth/userInfo"
    }),

    moduleName() {
      let data;
      data = this.selectedModule;
      return data;
    },
    sectionMeta() {
      const meta = {
        roles: {
          kicker: "Role Directory",
          title: "Clinical Team Roles"
        },
        permissions: {
          kicker: this.selectedModule || "Route Access",
          title: "Route Permissions Matrix"
        },
        route_permissions: {
          kicker: "Module Access",
          title: "Module Permissions Matrix"
        }
      };

      return meta[this.activeTab] || meta.roles;
    },
    roleCount() {
      return (this.listDoctors || []).length;
    },
    moduleTabs() {
      return this.modules.reduce((tabs, module) => {
        return tabs.concat(module.key || []);
      }, []);
    },
    moduleCount() {
      if (this.activeTab === "route_permissions") {
        return this.modulePermissions.length;
      }

      return this.moduleTabs.length;
    },
    routesList() {
      if (Array.isArray(this.routes)) {
        return this.routes;
      }

      return Object.values(this.routes || {});
    },
    routeCount() {
      return this.routesList.length;
    },
    Roles() {
      let data = [];
      (this.listDoctors || []).map(item => {
        data.push({
          name: item.name,
          description: item.description,
          created_on: formatedDate(item.created_at),
          parent: item?.parentLookup?.name
        });
      });
      return data;
    },
    parentRoles() {
      let data = [];
      (this.listDoctors || []).map(item => {
        data.push({
          key: item.id,
          name: item.name
        });
      });
      return data;
    },
    checkItem() {
      return function(val, val2) {
        if (val && val2 && val2 !== undefined) {
          const object = val2;
          return object[val.toLowerCase()];
        }
        return false;
      };
    },
    moduleCheck() {
      return function(val, val2) {
        if (val && val2 && val2 !== undefined) {
          const object = val2.permissions;
          return object[val.toLowerCase()];
        }
        return false;
      };
    }
  },
  methods: {
    ...mapActions({
      getEndPoints: "bussiness/getEndPoints",
      editPermissions: "bussiness/editPermissions",
      editModulePermissions: "bussiness/editModulePermissions",
      getPermissions: "bussiness/getPermissions",
      createPermissions: "bussiness/createPermissions",
      createModulePermissions: "bussiness/createModulePermissions",
      doctorList: "doctors/doctorList",
      addDoctors: "doctors/addDoctors"
    }),
    handleRoleChange(role, route, event) {
      const selectedRole = role?.name?.toLowerCase();
      Object.values(this.userModules || {}).forEach(item => {
        Object.values(item)?.filter(item2 => {
          if (item2.routeName === route.routeName) {
            item2.permissions = {
              ...item2.permissions,
              [selectedRole]: event.target.checked
            };
          }
        });
      });
    },
    handlePermissionChange(role, permission, event) {
      if (role && permission) {
        const selectedPermission = this.modulePermissions.filter(
          item => item.moduleName === permission.moduleName
        )[0];

        if (selectedPermission) {
          selectedPermission.permissions[role.name.toLowerCase()] =
            event.target.checked;
        }
      }
    },
    handleCreate() {
      this.role.type = "roles";
      this.addDoctors({
        ...this.role,
        isOther:
          this.$store &&
          this.$store.state &&
          this.$store.state.auth.user.isOther
      }).then(() => {
        this.doctorList("roles");
        this.$modal.hide("addnewrole");
        this.role = {};
      });
    },
    handleCancel() {
      this.role = {};
      this.$modal.hide("addnewrole");
    },
    handlePermissionClick(tab) {
      if (tab === "permissions") {
        const allModulesName = this.modules[0]?.data || {};

        const orgRoles = (this.listDoctors || []).map(role => role.name);

        const permissions = {};
        if ((this.rbacPermissions || []).length === 0) {
          for (const permission of orgRoles) {
            permissions[permission.toLowerCase().replace(" ", "_")] = true;
          }
          Object.values(allModulesName).map(item => {
            delete item.visibility;
            for (let key in item) {
              item[key].permissions = permissions;
              item[key].routeName = key;
            }
          });
          this.userModules = allModulesName;
        } else {
          this.userModules = this.rbacPermissions[0]?.permissions || {};
        }
        this.showSubTabs = true;
      } else {
        this.showSubTabs = false;
      }
      this.activeTab = tab;
    },
    routePermissionsClick(tab) {
      this.activeTab = tab;
    },
    handleRoleClick() {
      this.activeTab = "roles";
      this.showSubTabs = false;
    },
    async handleSubTabClick(data) {
      if (data) {
        if ((this.rbacPermissions || []).length === 0) {
          this.routes = this.lookupEndPoints?.[0]?.[data] || [];
          this.selectedModule = data;
        } else {
          this.selectedModule = data;
          this.routes = this.rbacPermissions[0]?.permissions?.[data] || [];
        }
      }
    },
    showPanelModal() {
      this.$modal.show("addnewrole");
    },
    editBusinessPermissions() {
      this.disablePermissions = false;
      if (!this.disablePermissions) {
        this.editButtonText = "Save Permissions";
      }
    },
    // editModulePermissions() {
    //   this.disablePermissions = false;
    //   if (!this.disablePermissions) {
    //     this.editButtonText = "Save Permissions";
    //   }
    // },
    async savePermissions() {
      if (this.activeTab === "permissions") {
        if ((this.rbacPermissions || []).length === 0) {
          await this.createPermissions({ payload: this.userModules });
        } else {
          await this.editPermissions({
            id: this.rbacPermissions[0]?.id,
            payload: this.userModules
          });
        }
      } else {
        if (!this.rbacPermissions[0]?.id) {
          await this.createModulePermissions({
            payload: this.modulePermissions
          });
        } else {
          await this.editModulePermissions({
            id: this.rbacPermissions[0]?.id,
            payload: this.modulePermissions
          });
        }
      }
      await this.getPermissions();
      this.disablePermissions = true;
      this.editButtonText = "Edit Permissions";
    }
  },
  async mounted() {
    await this.getEndPoints();
    await this.doctorList("roles");

    (this.lookupEndPoints || []).forEach(obj => {
      const keys = Object.keys(obj);
      this.modules.push({ key: keys, data: obj });
    });

    this.selectedModule = this.modules[0]?.key[0];

    const userDto = Object.keys(this.modules[0]?.data || {});

    await this.getPermissions();

    if (this.rbacPermissions[0]?.module_permissions?.module_permissions) {
      const permissionsDto = this.rbacPermissions[0]?.module_permissions
        ?.module_permissions;
      this.modulePermissions = permissionsDto;
    } else {
      this.modulePermissions = this.mp;
    }

    if ((this.rbacPermissions || []).length !== 0) {
      this.routes = this.rbacPermissions[0]?.permissions?.[userDto[0]] || [];
    } else {
      Object.values(this.lookupEndPoints?.[0] || {})?.forEach(dto => {
        delete dto.visibility;
        Object.keys(dto).forEach(key2 => {
          dto[key2].routeName = key2;
        });
      });
      this.routes = this.lookupEndPoints?.[0]?.[userDto[0]] || [];
    }
  }
};
</script>

<style scoped>
.rbac-page {
  min-height: 100vh;
  background: var(--canvas);
}

.rbac-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.rbac-layout {
  display: grid;
  grid-template-columns: 232px minmax(0, 1fr);
  gap: 18px;
  padding: 18px 24px 24px;
}

.rbac-nav {
  position: sticky;
  top: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: var(--surface);
  box-shadow: var(--shadow-sm);
}

.rbac-tab {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 42px;
  padding: 10px 12px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: 600;
  line-height: 1.25;
  text-align: left;
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease);
}

.rbac-tab .mdi {
  flex: 0 0 18px;
  font-size: 18px;
  color: var(--muted-soft);
}

.rbac-tab:hover,
.rbac-tab.active {
  background: var(--accent-050);
  color: var(--accent-600);
}

.rbac-tab.active .mdi {
  color: var(--accent-600);
}

.rbac-content {
  min-width: 0;
}

.rbac-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.rbac-kicker {
  margin: 0 0 4px;
  color: var(--muted);
  font-size: var(--fs-xs);
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: var(--tracking-eyebrow);
}

.rbac-header h2 {
  margin: 0;
  color: var(--ink-900);
  font-size: var(--fs-h2);
  font-weight: 700;
  line-height: 1.2;
}

.rbac-mode {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 4px 10px;
  border: 1px solid var(--line);
  border-radius: 99px;
  background: var(--surface);
  color: var(--muted);
  font-size: var(--fs-xs);
  font-weight: 700;
  line-height: 1;
}

.rbac-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.rbac-metric {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 54px;
  padding: 11px 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: var(--surface);
}

.rbac-metric span {
  color: var(--muted);
  font-size: var(--fs-sm);
  font-weight: 600;
}

.rbac-metric strong {
  color: var(--ink-900);
  font-size: var(--fs-h2);
  font-weight: 700;
  line-height: 1;
}

.rbac-section {
  min-width: 0;
}

.rbac-modal-body {
  padding-top: 4px;
}

.rbac-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
}

.rbac-modal-actions ::v-deep .button--transparent span {
  color: var(--text);
}

.rbac-loading {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: var(--surface);
}

.permissions-layout {
  display: grid;
  grid-template-columns: 210px minmax(0, 1fr);
  gap: 14px;
  align-items: start;
}

.module-list {
  max-height: calc(100vh - 24rem);
  overflow: auto;
  padding: 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: var(--surface);
  box-shadow: var(--shadow-sm);
}

.module-list-title {
  padding: 4px 8px 10px;
  color: var(--muted);
  font-size: var(--fs-xs);
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: var(--tracking-eyebrow);
}

.module-pill {
  display: block;
  width: 100%;
  min-height: 36px;
  margin: 0 0 4px;
  padding: 8px 10px;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: 600;
  line-height: 1.25;
  text-align: left;
  transition: background var(--dur) var(--ease),
    border-color var(--dur) var(--ease), color var(--dur) var(--ease);
}

.module-pill:hover,
.module-pill.active {
  border-color: var(--accent-100);
  background: var(--accent-050);
  color: var(--accent-600);
}

.permission-card {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: var(--surface);
  box-shadow: var(--shadow-sm);
}

.permission-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--line);
  background: var(--surface);
}

.permission-card-header span {
  min-width: 0;
  overflow: hidden;
  color: var(--ink-900);
  font-size: var(--fs-body);
  font-weight: 700;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.permission-card-header strong {
  flex: 0 0 auto;
  color: var(--muted);
  font-size: var(--fs-xs);
  font-weight: 700;
  line-height: 1;
}

.permission-table-wrap {
  max-height: calc(100vh - 25rem);
  overflow: auto;
}

.permissions-table {
  width: 100%;
  min-width: 740px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: var(--fs-sm);
  font-variant-numeric: tabular-nums;
}

.permissions-table th,
.permissions-table td {
  padding: 10px 12px;
  border-right: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  text-align: left;
  vertical-align: middle;
}

.permissions-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--surface-2);
  color: var(--muted);
  font-size: var(--fs-xs);
  font-weight: 700;
  line-height: 1.25;
  text-transform: uppercase;
  letter-spacing: var(--tracking-eyebrow);
  white-space: nowrap;
}

.permissions-table td {
  background: var(--surface);
  color: var(--text);
  font-size: var(--fs-sm);
  font-weight: 500;
  line-height: 1.35;
}

.permissions-table tbody tr:hover td {
  background: var(--accent-050);
}

.permissions-table tbody tr:last-child td {
  border-bottom: 0;
}

.permission-name {
  position: sticky;
  left: 0;
  z-index: 1;
  min-width: 220px;
  width: 280px;
}

.permissions-table th.permission-name {
  z-index: 3;
}

.permissions-table td.permission-name {
  color: var(--ink-900);
  font-weight: 700;
}

.permission-role,
.permission-check {
  min-width: 124px;
  text-align: center !important;
}

.permission-checkbox {
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: var(--accent-600);
  cursor: pointer;
}

.permission-checkbox:disabled {
  cursor: default;
  opacity: 0.55;
}

.permission-empty {
  padding: 28px 16px !important;
  background: var(--surface) !important;
  color: var(--muted) !important;
  font-size: var(--fs-sm);
  font-weight: 600;
  text-align: center !important;
}

@media screen and (max-width: 1100px) {
  .rbac-layout {
    grid-template-columns: 1fr;
  }

  .rbac-nav {
    position: static;
    flex-direction: row;
    overflow-x: auto;
  }

  .rbac-tab {
    flex: 0 0 auto;
    width: auto;
  }
}

@media screen and (max-width: 780px) {
  .rbac-layout {
    padding: 14px 16px 20px;
  }

  .rbac-header,
  .rbac-metrics,
  .permissions-layout {
    grid-template-columns: 1fr;
  }

  .rbac-header {
    display: block;
  }

  .rbac-mode {
    margin-top: 10px;
  }

  .permissions-layout {
    display: grid;
  }

  .module-list {
    max-height: 220px;
  }
}
</style>
